import React, { useEffect, useState } from "react";
import Head from "next/head";
// Components
import { TopbarLanding } from "../../components/LandingPage/topbar";
import { getPaginatedPosts } from "../../services/CMSBlogService";

import Banner from "./home/components/Banner";
import Blog from "../../components/LandingPage/Blog/Blog";
import SuccessCases from "./home/components/SuccessCases";
import VerzelDna from "../../components/LandingPage/VerzelDna/VerzelDna";
import { Footer } from "../../components/LandingPage/footer";
import { BusinessDivision } from "../../components/LandingPage/BusinessDivision/BusinessDivision";
import { getI18nProps, getStaticPaths } from "../../lib/next-i18next/getStatic";
import { Contact } from "../../components/LandingPage/Contact";
import WhatsAppButton from "../../components/WhatsAppButton";
import { FaceLiftGlobalStyle } from "../../styles/theme";
import DefaultDarkModeHOC from "../../components/theme/DefaultDarkModeHOC";

const HomePageMeta = ({ translations }) => {
	return (
		<Head>
			<title>{translations.meta.title}</title>
			<meta name="title" content={translations.meta.title} />
			<meta name="description" content={translations.meta.description} />

			<meta property="og:type" content="website" />
			<meta
				property="og:url"
				content={process.env.NEXT_PUBLIC_WEBSITE_DOMAIN}
			/>
			<meta property="og:title" content={translations.meta.title} />
			<meta property="og:description" content={translations.meta.description} />
			<meta
				property="og:image"
				content={`${process.env.NEXT_PUBLIC_WEBSITE_DOMAIN}/landings/assets/images/LogoVerzelHorizontal.svg`}
			/>

			<meta property="twitter:card" content="summary_large_image" />
			<meta
				property="twitter:url"
				content={process.env.NEXT_PUBLIC_WEBSITE_DOMAIN}
			/>
			<meta property="twitter:title" content={translations.meta.title} />
			<meta
				property="twitter:description"
				content={translations.meta.description}
			/>
			<meta
				property="twitter:image"
				content={`${process.env.NEXT_PUBLIC_WEBSITE_DOMAIN}/landings/assets/images/LogoVerzelHorizontal.svg`}
			/>
		</Head>
	);
};

const HomePage = ({ blogPosts, _nextI18Next, locale }) => {
	const translations = _nextI18Next.initialI18nStore[locale].translation;
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	/* A seção de contato só monta no cliente (ver `mounted && <Contact />`), então
	   o scroll nativo para "#contact-us" roda antes do elemento existir e o
	   usuário fica no topo da home. Refazemos o scroll quando ela entra no DOM,
	   após um frame para a seção já ter altura. */
	useEffect(() => {
		if (!mounted || window.location.hash !== "#contact-us") return;

		const frame = requestAnimationFrame(() => {
			document
				.getElementById("contact-us")
				?.scrollIntoView({ behavior: "smooth", block: "start" });
		});
		return () => cancelAnimationFrame(frame);
	}, [mounted]);

	return (
		<DefaultDarkModeHOC>
			<HomePageMeta translations={translations} />
			<FaceLiftGlobalStyle />
			<TopbarLanding />
			<Banner />
			<BusinessDivision />
			<VerzelDna />
			<Blog posts={blogPosts} />
			<SuccessCases />
			{mounted && <Contact />}
			<WhatsAppButton />
			<Footer />
		</DefaultDarkModeHOC>
	);
};

export const getStaticProps = async (ctx) => {
	const i18nProps = await getI18nProps(ctx);
	const postsResponse = await getPaginatedPosts({
		search: undefined,
		limit: 9,
		page: 1,
		locale: i18nProps._nextI18Next.initialLocale,
	});

	let blogPosts = postsResponse?.posts ?? [];
	// dev-only: keep the media carousel navigable when the CMS is
	// unreachable locally (e.g. missing .env) — never ships to production
	if (blogPosts.length === 0 && process.env.NODE_ENV === "development") {
		const { samplePosts } = await import("../../data/sampleBlogPosts");
		blogPosts = samplePosts;
	}

	return {
		props: {
			...i18nProps,
			blogPosts,
			locale: i18nProps._nextI18Next.initialLocale,
		},
	};
};

export { getStaticPaths };

export default HomePage;
